import React, { useState } from 'react';
import styled from 'styled-components';
import { useAccount, useChainId } from 'wagmi';
import { useSwitchChain } from 'wagmi';
import DepositTab from '../components/mavrkpay/DepositTab';
import PayTab from '../components/mavrkpay/PayTab';
import RequestTab from '../components/mavrkpay/RequestTab';
import { useUsdgBalance } from '../hooks/useUsdgBalance';
import { ZNSAddressDisplay } from '../components/shared/ZNSAddressDisplay';
import { PageShell } from '../components/shared/PageShell';

const INK_CHAIN_ID = 57073;

const PayContent = styled.div`
  width: 100%;
  max-width: 600px;
  background: rgba(20, 20, 30, 0.4);
  border-radius: 20px;
  border: 1px solid rgba(168, 85, 247, 0.2);
  padding: 0;
  color: #ffffff;
  font-family: 'Plus Jakarta Sans', sans-serif;
  box-shadow: 0 0 30px rgba(168, 85, 247, 0.15);
  backdrop-filter: blur(12px);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(168, 85, 247, 0.6), transparent);
  }
  
  @media (max-width: 768px) {
    max-width: 100%;
    border-radius: 16px;
  }
`;

const PageHeader = styled.div`
  padding: 32px 24px 24px 24px;
  border-bottom: 1px solid rgba(168, 85, 247, 0.2);
  
  @media (max-width: 768px) {
    padding: 24px 16px 20px 16px;
  }
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 300;
  color: #ffffff;
  margin: 0 0 8px 0;
  text-shadow: 0 0 20px rgba(255, 255, 255, 0.2);
  
  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const Subtitle = styled.p`
  font-size: 15px;
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
  font-weight: 300;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const WalletInfo = styled.div`
  padding: 20px 24px;
  background: rgba(168, 85, 247, 0.05);
  border-bottom: 1px solid rgba(168, 85, 247, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const WalletAddress = styled.div`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  font-family: 'monospace';
  
  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const BalanceDisplay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const BalanceAmount = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: var(--color-neon-green);
  text-shadow: 0 0 10px rgba(10, 255, 10, 0.3);
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const BalanceUSD = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  
  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const NetworkWarning = styled.div`
  padding: 16px 24px;
  background: rgba(251, 191, 36, 0.1);
  border-left: 3px solid #fbbf24;
  margin: 0 24px 20px 24px;
  border-radius: 8px;
  
  @media (max-width: 768px) {
    margin: 0 16px 16px 16px;
    padding: 12px 16px;
  }
`;

const SwitchNetworkButton = styled.button`
  margin-top: 8px;
  padding: 8px 16px;
  background: rgba(168, 85, 247, 0.3);
  border: 1px solid var(--color-neon-purple);
  border-radius: 8px;
  color: #ffffff;
  font-size: 13px;
  font-weight: 300;
  cursor: pointer;
  transition: all 0.2s ease;
  backdrop-filter: blur(12px);
  text-shadow: 0 0 8px rgba(188, 19, 254, 0.2);
  
  &:hover {
    background: rgba(168, 85, 247, 0.5);
    box-shadow: 0 0 20px rgba(168, 85, 247, 0.3);
  }
`;

const TabsContainer = styled.div`
  display: flex;
  padding: 0 24px;
  gap: 8px;
  border-bottom: 1px solid rgba(168, 85, 247, 0.2);
  margin-top: 20px;
  
  @media (max-width: 768px) {
    padding: 0 16px;
  }
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 14px 24px;
  background: ${props => props.$active ? 'rgba(168, 85, 247, 0.2)' : 'transparent'};
  border: none;
  border-bottom: 2px solid ${props => props.$active ? 'var(--color-neon-purple)' : 'transparent'};
  color: ${props => props.$active ? '#ffffff' : 'rgba(255, 255, 255, 0.6)'};
  font-size: 15px;
  font-weight: ${props => props.$active ? '600' : '300'};
  cursor: pointer;
  transition: all 0.2s ease;
  flex: 1;
  font-family: 'Plus Jakarta Sans', sans-serif;
  text-shadow: ${props => props.$active ? '0 0 10px rgba(188, 19, 254, 0.2)' : 'none'};
  
  &:hover {
    background: rgba(168, 85, 247, 0.1);
    color: #ffffff;
  }
  
  @media (max-width: 768px) {
    padding: 12px 16px;
    font-size: 14px;
  }
`;

const TabContent = styled.div`
  padding: 32px 24px;
  min-height: 500px;
  
  @media (max-width: 768px) {
    padding: 24px 16px;
  }
`;

const PayPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'deposit' | 'pay' | 'request'>('deposit');
  const { address, isConnected } = useAccount();
  const chainId = useChainId();
  const { switchChain } = useSwitchChain();
  const { balance, balanceUSD, refetch } = useUsdgBalance(address);

  const isOnInk = chainId === INK_CHAIN_ID;

  const handleSwitchToInk = () => {
    if (switchChain) {
      switchChain({ chainId: INK_CHAIN_ID });
    }
  };

  return (
    <PageShell className="items-center">
      <PayContent>
        <PageHeader>
          <Title>MavrkPay</Title>
          <Subtitle>Deposit, pay and request in USDG on Ink.</Subtitle>
        </PageHeader>

        {isConnected && (
          <WalletInfo>
            <WalletAddress>
              {address ? (
                <ZNSAddressDisplay
                  address={address}
                  truncateLength={6}
                  showIcon={true}
                  iconSize={14}
                />
              ) : (
                'Not connected'
              )}
            </WalletAddress>
            <BalanceDisplay>
              <BalanceAmount>{balance} USDG</BalanceAmount>
              <BalanceUSD>${balanceUSD}</BalanceUSD>
            </BalanceDisplay>
          </WalletInfo>
        )}

        {isConnected && !isOnInk && (
          <NetworkWarning>
            <div style={{ fontSize: '14px', color: '#fbbf24', marginBottom: '4px', fontWeight: '600' }}>
              Wrong Network
            </div>
            <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.7)' }}>
              Please switch to Ink to use MavrkPay
            </div>
            <SwitchNetworkButton onClick={handleSwitchToInk}>
              Switch to Ink
            </SwitchNetworkButton>
          </NetworkWarning>
        )}

        <TabsContainer>
          <Tab $active={activeTab === 'deposit'} onClick={() => setActiveTab('deposit')}>
            Deposit
          </Tab>
          <Tab $active={activeTab === 'pay'} onClick={() => setActiveTab('pay')}>
            Pay
          </Tab>
          <Tab $active={activeTab === 'request'} onClick={() => setActiveTab('request')}>
            Request
          </Tab>
        </TabsContainer>

        <TabContent>
          {activeTab === 'deposit' && <DepositTab onSuccess={refetch} />}
          {activeTab === 'pay' && <PayTab userAddress={address} balance={balance} onSuccess={refetch} />}
          {activeTab === 'request' && <RequestTab userAddress={address} />}
        </TabContent>
      </PayContent>
    </PageShell>
  );
};

export default PayPage;

